import { clsx } from 'clsx'

import { isAnnualOnly } from '@bettermode/domain/billing/plan-offer'
import {
  BillingPriceInterval,
  type BillingProduct,
} from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { useCurrentProductInformation } from '../hooks/useCurrentProductInformation.js'
import {
  isTrialConversionTarget,
  PlanCardButton,
} from '../PlansPage/PlanCardButton.js'
import { PlanCardButtonLabel } from '../PlansPage/PlanCardButtonLabel.js'

import { PlanPricing } from './PlanPricing.js'
import { getCurrentPrice, getPlanPricing } from './pricing.js'
import { TrialStatusAlert } from './TrialStatusAlert.js'

const CONTACT_SALES_URL = 'https://bettermode.com/contact-sales'

interface Props {
  product: BillingProduct
  interval: BillingPriceInterval
}

interface PriceSlotProps {
  product: BillingProduct
  interval: BillingPriceInterval
  pricing: ReturnType<typeof getPlanPricing>
  yearlyPricing: ReturnType<typeof getPlanPricing>
  reservedMonthlyAmount: number
  currentPrice: BillingProduct['prices'][number] | undefined
}

const monthlyAmountOf = (price: BillingProduct['prices'][number]): number =>
  price.interval === BillingPriceInterval.Month
    ? price.amount / 100
    : price.amount / 100 / 12

const yearlyTotalOf = (price: BillingProduct['prices'][number]): number =>
  price.interval === BillingPriceInterval.Month
    ? (price.amount / 100) * 12
    : price.amount / 100

const PlanCardPrice = ({
  product,
  interval,
  pricing,
  yearlyPricing,
  reservedMonthlyAmount,
  currentPrice,
}: PriceSlotProps) => {
  if (product.isEnterprise) {
    return (
      <div className="text-[2.125rem] font-semibold tracking-[-0.03em] text-content">
        <T defaultMessage="Contact us" id="Generics.ContactUs" />
      </div>
    )
  }

  if (currentPrice) {
    return (
      <PlanPricing
        monthlyAmount={monthlyAmountOf(currentPrice)}
        interval={interval}
        reservedMonthlyAmount={reservedMonthlyAmount}
        reservedSavingsPercent={null}
        savingsPercent={null}
        yearlyTotal={yearlyTotalOf(currentPrice)}
      />
    )
  }

  // No offered price at this interval. An annual-only plan (yearly offered, monthly not) reads
  // "Only Annually"; any other not-offered case — a misconfigured or legacy plan with no default
  // price at the selected interval — reads "Contact us". Never a "$0/m" for a paid plan.
  if (!pricing.price) {
    return (
      <div className="text-[2.125rem] font-semibold tracking-[-0.03em] text-content">
        {isAnnualOnly(product.prices) ? (
          <T
            defaultMessage="Only Annually"
            id="Admin.Billing.PlanCards.OnlyAnnually"
          />
        ) : (
          <T defaultMessage="Contact us" id="Generics.ContactUs" />
        )}
      </div>
    )
  }

  return (
    <PlanPricing
      monthlyAmount={pricing.monthlyAmount}
      interval={interval}
      monthlyPrice={pricing.monthlyPrice}
      reservedMonthlyAmount={reservedMonthlyAmount}
      reservedSavingsPercent={yearlyPricing.savingsPercent}
      savingsPercent={pricing.savingsPercent}
      yearlyTotal={pricing.yearlyTotal}
    />
  )
}

export const PlanCard = ({ product, interval }: Props) => {
  const currentPricing = getPlanPricing(product, interval)
  const yearlyPricing = getPlanPricing(product, BillingPriceInterval.Year)
  const monthlyPricing = getPlanPricing(product, BillingPriceInterval.Month)
  const { price } = currentPricing
  const reservedMonthlyAmount = Math.max(
    monthlyPricing.monthlyAmount,
    yearlyPricing.monthlyAmount,
  )
  const {
    currentProduct,
    currentInterval,
    currentPlanPriceId,
    isTrial,
    isManual,
  } = useCurrentProductInformation()
  // Shares the button's predicate so the alert and the CTA agree about what a trial
  // converts into. Reading `upgradedPlanId` directly would leave native trials — where
  // it is undefined — with a Subscribe button but no trial status or expiry alert.
  const isTrialPlan = isTrialConversionTarget({
    isTrial,
    isManual,
    currentProduct,
    newProduct: product,
  })
  // A sales-made subscription can sit on a non-default price. That price is never offered,
  // but the customer's own card at their own interval must still read as the current plan.
  // Never for a trialer: their subscription sits on the plan they are evaluating, and an
  // annual-only plan must show "Only Annually" on the Monthly tab, not the trial's own price.
  // Safe today only because native trials are always yearly (so interval never matches Month);
  // the `!isTrial` guard keeps it correct if that ever changes.
  const isOwnCardAtOwnInterval =
    !price &&
    !isTrial &&
    currentProduct?.id === product.id &&
    currentInterval === interval
  const currentPrice = isOwnCardAtOwnInterval
    ? getCurrentPrice(product, currentPlanPriceId)
    : undefined
  const buttonPrice = price ?? currentPrice

  return (
    <div
      className={clsx(
        'relative flex h-full flex-col rounded-[18px] border px-5 pb-5 pt-6',
        product.popular
          ? 'border-line-positive-subdued bg-[linear-gradient(180deg,var(--bm-color-surface-positive-subdued)_0%,var(--bm-color-surface)_70%)] shadow-sm'
          : 'border-line-subdued bg-surface',
      )}
    >
      {product.popular && (
        <div className="absolute left-4 top-0 -translate-y-1/2">
          <span className="inline-block rounded-full bg-surface-positive px-2.5 py-1 text-[11px] font-medium text-content-on-positive">
            <T
              defaultMessage="Recommended"
              id="Admin.Billing.PlanCards.Recommended"
            />
          </span>
        </div>
      )}

      <div className="pb-3">
        <h3 className="text-[1.0625rem] font-medium text-content">
          {product.name}
        </h3>
      </div>

      {/* Floor the price slot so a one-line state ("Only Annually", "Contact us") reserves the
          same height as a price line, and the features below don't jump when the interval
          toggles. Annual-only plans never carry a savings row, so one line is the tallest state. */}
      <div className="min-h-[3.25rem] pb-5">
        <PlanCardPrice
          product={product}
          interval={interval}
          pricing={currentPricing}
          yearlyPricing={yearlyPricing}
          reservedMonthlyAmount={reservedMonthlyAmount}
          currentPrice={currentPrice}
        />
      </div>

      <div className="flex flex-grow flex-col gap-2.5">
        {product.features.map(feature => (
          <div
            key={feature}
            className="flex items-start gap-2 text-content-subdued"
          >
            <div className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-line-subdued">
              <SvgIcon name="check" className="size-2.5 text-content-subdued" />
            </div>
            <span className="text-xs leading-relaxed text-content-subdued">
              {feature}
            </span>
          </div>
        ))}
      </div>

      {product.imageUrls?.length > 0 && (
        <div className="pb-5 pt-6">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-content-subdued">
            <T
              defaultMessage="integrations"
              id="Admin.Billing.PlanCards.Integrations"
            />
          </p>
          <div className="flex flex-wrap gap-1.5">
            {product.imageUrls.map(url => (
              <img
                key={url}
                src={url}
                alt=""
                className="size-[18px] rounded-sm object-contain"
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-auto flex flex-col gap-2 pt-6">
        {isTrialPlan && <TrialStatusAlert />}
        {product.isEnterprise || !buttonPrice ? (
          <a
            href={CONTACT_SALES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-11 items-center justify-center rounded-full bg-surface-inverse px-4 text-sm font-medium text-content-on-inverse transition hover:opacity-90"
          >
            <PlanCardButtonLabel variant="contact" />
          </a>
        ) : (
          <PlanCardButton
            newProduct={product}
            price={buttonPrice}
            className={clsx(
              'flex h-11 items-center justify-center rounded-full text-sm !border-transparent shadow-none',
              product.popular
                ? '!bg-action-primary !text-content-on-interactive hover:!bg-action-primary-hovered'
                : '!bg-surface-inverse !text-content-on-inverse hover:!opacity-90',
            )}
          />
        )}
        <div className="flex justify-center">
          <Link
            href="https://bettermode.com/pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-content-subdued hover:text-content"
          >
            <T
              defaultMessage="See details"
              id="Admin.Billing.PlanCards.SeeDetails"
            />
            <SvgIcon name="chevron-right" className="size-3" />
          </Link>
        </div>
      </div>
    </div>
  )
}
